import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, ChevronDown, ChevronRight, Check, Edit, Trash2, Loader2, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocumentChecklistFormModal } from "./document-checklist-form-modal";
import { DocumentChecklistItemFormModal } from "./document-checklist-item-form-modal";
import type { DocumentChecklist, DocumentChecklistItem, LenderProduct } from "@shared/schema";

interface DocumentsPanelProps {
  lenderId: string;
}

type ChecklistWithProduct = DocumentChecklist & { productNames: string[] }; // ISSUE-069: Changed from single productName to array

export function DocumentsPanel({ lenderId }: DocumentsPanelProps) {
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistWithProduct | undefined>();
  const [selectedItem, setSelectedItem] = useState<DocumentChecklistItem | undefined>();
  const [activeChecklistId, setActiveChecklistId] = useState<string | null>(null);
  const [expandedChecklists, setExpandedChecklists] = useState<Set<string>>(new Set());
  const [deleteChecklistId, setDeleteChecklistId] = useState<string | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);

  const { toast } = useToast();

  // Fetch products for this lender (to check if any exist)
  const { data: products = [] } = useQuery<LenderProduct[]>({
    queryKey: ["/api/crm/lenders", lenderId, "products"],
  });

  // Fetch all checklists for this lender
  const { data: checklists = [], isLoading: isLoadingChecklists } = useQuery<ChecklistWithProduct[]>({
    queryKey: ["/api/crm/lenders", lenderId, "document-checklists"],
  });

  // Fetch items for active checklist
  const { data: items = [], isLoading: isLoadingItems } = useQuery<DocumentChecklistItem[]>({
    queryKey: ["/api/crm/document-checklists", activeChecklistId, "items"],
    enabled: !!activeChecklistId,
  });

  // Delete checklist mutation
  const deleteChecklistMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/document-checklists/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/lenders", lenderId, "document-checklists"] });
      toast({ title: "Checklist deleted", description: "The document checklist and all its items have been deleted." });
      setDeleteChecklistId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete checklist", variant: "destructive" });
    },
  });

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/document-checklist-items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/document-checklists", activeChecklistId, "items"] });
      toast({ title: "Document deleted", description: "The document has been removed from the checklist." });
      setDeleteItemId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete document", variant: "destructive" });
    },
  });

  const toggleChecklistExpansion = (checklistId: string) => {
    const newExpanded = new Set(expandedChecklists);
    if (newExpanded.has(checklistId)) {
      newExpanded.delete(checklistId);
      if (activeChecklistId === checklistId) {
        setActiveChecklistId(null);
      }
    } else {
      newExpanded.add(checklistId);
      setActiveChecklistId(checklistId);
    }
    setExpandedChecklists(newExpanded);
  };

  const handleEditChecklist = (checklist: ChecklistWithProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedChecklist(checklist);
    setShowChecklistForm(true);
  };

  const handleEditItem = (item: DocumentChecklistItem) => {
    setSelectedItem(item);
    setShowItemForm(true);
  };

  const handleAddItem = (checklistId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveChecklistId(checklistId);
    setSelectedItem(undefined);
    setShowItemForm(true);
  };

  const handleCloseChecklistModal = () => {
    setShowChecklistForm(false);
    setSelectedChecklist(undefined);
  };

  const handleCloseItemModal = () => {
    setShowItemForm(false);
    setSelectedItem(undefined);
  };

  if (isLoadingChecklists) {
    return <div className="p-4">Loading document checklists...</div>;
  }

  // Check if there are any products
  if (products.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Document Checklists</h3>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-3 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">No Products Available</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Document checklists are created per product. Please add at least one product to this lender before creating document checklists.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Go to the <strong>Products</strong> tab to add a product first.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Document Checklists</h3>
        <Button
          size="sm"
          onClick={() => {
            setSelectedChecklist(undefined);
            setShowChecklistForm(true);
          }}
          data-testid="button-add-checklist"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Checklist
        </Button>
      </div>

      {checklists.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No document checklists found. Click "Add Checklist" to create one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {checklists.map((checklist) => {
            const isExpanded = expandedChecklists.has(checklist.id);
            const checklistItems = activeChecklistId === checklist.id ? items : [];

            return (
              <Card key={checklist.id}>
                <CardHeader
                  className="pb-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => toggleChecklistExpansion(checklist.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 flex items-start gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CardTitle
                            className="text-base hover:text-primary"
                            onClick={(e) => handleEditChecklist(checklist, e)}
                          >
                            {checklist.checklistName}
                          </CardTitle>
                          {/* ISSUE-069: Display multiple product names */}
                          {checklist.productNames && checklist.productNames.length > 0 ? (
                            checklist.productNames.map((productName, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {productName}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline" className="text-xs">No products</Badge>
                          )}
                          {checklist.isActive === 1 && <Badge variant="default">Active</Badge>}
                        </div>
                        {checklist.description && (
                          <p className="text-sm text-muted-foreground mt-1">{checklist.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isExpanded && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => handleAddItem(checklist.id, e)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Document
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleEditChecklist(checklist, e)}
                        data-testid={`button-edit-checklist-${checklist.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteChecklistId(checklist.id);
                        }}
                        data-testid={`button-delete-checklist-${checklist.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="pt-0">
                    {isLoadingItems ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        Loading documents...
                      </div>
                    ) : checklistItems.length === 0 ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        No documents in this checklist. Click "Add Document" to add one.
                      </div>
                    ) : (
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow className="h-9 bg-muted/50">
                              <TableHead className="text-xs font-medium px-3 py-1.5 w-[50px]">#</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5">Document Name</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5 text-center w-[100px]">Mandatory</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5 w-[80px]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {checklistItems.map((item, index) => (
                              <TableRow
                                key={item.id}
                                className="h-10 cursor-pointer hover:bg-accent/50 transition-colors"
                                data-testid={`row-item-${item.id}`}
                              >
                                <TableCell className="px-3 py-1.5 text-muted-foreground text-sm">
                                  {index + 1}
                                </TableCell>
                                <TableCell className="px-3 py-1.5">
                                  <div>
                                    <span className="font-medium text-sm">{item.documentName}</span>
                                    {item.description && (
                                      <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="px-3 py-1.5 text-center">
                                  {item.isRequired === 1 ? (
                                    <Badge variant="default" className="text-xs">
                                      <Check className="h-3 w-3 mr-1" />
                                      Required
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">Optional</span>
                                  )}
                                </TableCell>
                                <TableCell className="px-3 py-1.5">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditItem(item);
                                      }}
                                      data-testid={`button-edit-item-${item.id}`}
                                    >
                                      <Edit className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteItemId(item.id);
                                      }}
                                      data-testid={`button-delete-item-${item.id}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <DocumentChecklistFormModal
        open={showChecklistForm}
        onOpenChange={handleCloseChecklistModal}
        lenderId={lenderId}
        checklist={selectedChecklist}
      />

      {activeChecklistId && (
        <DocumentChecklistItemFormModal
          open={showItemForm}
          onOpenChange={handleCloseItemModal}
          checklistId={activeChecklistId}
          item={selectedItem}
          nextSortOrder={items.length + 1}
        />
      )}

      {/* Delete Checklist Confirmation Dialog */}
      <Dialog open={!!deleteChecklistId} onOpenChange={(open) => !open && setDeleteChecklistId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document Checklist</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this checklist? All documents within this checklist will also be deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteChecklistId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteChecklistId && deleteChecklistMutation.mutate(deleteChecklistId)}
              disabled={deleteChecklistMutation.isPending}
              data-testid="button-confirm-delete-checklist"
            >
              {deleteChecklistMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Confirmation Dialog */}
      <Dialog open={!!deleteItemId} onOpenChange={(open) => !open && setDeleteItemId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this document from the checklist? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItemId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteItemId && deleteItemMutation.mutate(deleteItemId)}
              disabled={deleteItemMutation.isPending}
              data-testid="button-confirm-delete-item"
            >
              {deleteItemMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
